<template>
  <p :class="[{ simple: isSimple, tdk2: !isSimple }]">
    <span
      :class="[
        { spacebar: spacebarNow && isSimple },
        'noselect',
        `char${i + 1}`,
        getClasses(i, letter)
      ]"
      v-for="(letter, i) in splitText"
      :key="letter + i"
      aria-hidden="true"
      v-html="letter"
    ></span>
  </p>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { characterMap } from 'constants/standard-keyboard';

const activity = createNamespacedHelpers('activity');
const typingActivity = createNamespacedHelpers('activity/typing');

export default {
  props: {
    text: {
      type: String,
      default: ''
    },
    answer: {
      type: String,
      require: false
    },
    textStep: {
      type: Number,
      required: true
    },
    hintClass: {
      type: String,
      required: false
    },
    isSimple: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      cursorColorInit: '', // Boolean
      typeSizeInit: '',
      letterSpacingInit: '',
      txtInit: '',
      canSelect: true,
      enabled: false,
      colorsDark: ['F1A12E', 'FDDE28', 'AED067', '8DB6D1', '958DB9'],
      errorColor: 'cc0000',
      cursorPos: '',
      noSelection: true,
      selector: '',
      cursorSelector: '',
      spacebarNow: false,
      characterMap
    };
  },

  computed: {
    ...activity.mapState(['progress']),
    ...typingActivity.mapState({
      typingProgress: 'progress',
      attemptedKey: 'attemptedKey'
    }),

    isCurrentStep() {
      return this.progress.stepIndex === this.textStep;
    },

    isCompletedStep() {
      return this.progress.stepIndex > this.textStep;
    },

    isWrongKey() {
      return this.attemptedKey.character && !this.attemptedKey.isCorrect;
    },

    hasBeenCorrectlyTyped() {
      return index => this.isCurrentStep && this.typingProgress.currentIndex > index;
    },

    splitText() {
      return (this.text || '').replace(' ', '\xa0').split('');
    }
  },

  methods: {
    getHintAdditionalClass() {
      return this.hintClass ? this.hintClass : '';
    },

    getRowClass(letter) {
      return characterMap.get(letter.toLowerCase());
    },

    isCurrentCharacter(index) {
      let result = this.isCurrentStep && this.typingProgress.currentIndex === index;
      if (this.answer) {
        result = result && this.typingProgress.currentIndex < this.answer.length;
      }
      return result;
    },

    getClasses(index, letter) {
      const trueLetter = letter === '\xa0' ? ' ' : letter;
      const isCurrentCharacter = this.isCurrentCharacter(index);
      const classes = {
        expected: isCurrentCharacter,
        charDone: this.hasBeenCorrectlyTyped(index) || this.isCompletedStep,
        error: this.isSimple ? false : isCurrentCharacter && this.isWrongKey,
        spacebar: this.isSimple ? false : trueLetter === ' ',
        [this.getRowClass(trueLetter)]: true,
        hint: this.isSimple ? false : isCurrentCharacter && this.getHintAdditionalClass()
      };
      return classes;
    }
  }
};
</script>

<style lang="scss" scoped>
.noselect {
  -webkit-touch-callout: none;
  user-select: none;
  cursor: default;
}

p {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  font-family: hwtprint;
}

// BASE PLUS CURSORHEIGHT
p.tdk2 {
  color: black;
}
p.tdk2 span {
  border: 1px solid white;
}

.spacebar {
  min-width: 25px;
}

p.tdk2 span.charDone {
  color: black;
}
p .hint {
  position: relative;
  &:after {
    content: '';
    position: absolute;
    top: -100px;
    left: 40px;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    background-color: #8db6d2;
  }
}
// Expected key highlighted in a color-box
.expected {
  margin-right: 10px;
  background-repeat: no-repeat;
  background-color: white;
  color: black;
  background-size: contain;
  background-position: center center;
  // padding: remValues(15, 50);
  padding: 15px 50px;
  // Number Row Keys
  &.row1 {
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmL\
TgiPz48c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZ\
y8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBwe\
CIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMTI4IDEyOCIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgM\
TI4IDEyOCIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+PHBhdGggZmlsbD0iI0ZBRDRBNCIgZD0iTTEyMC4zL\
DI3LjV2NzMuMWMwLDguOC03LjEsMTUuOS0xNS45LDE1LjlIMjMuNmMtOC44LDAtMTUuOS03LjEtMTUuO\
S0xNS45VjI3LjVjMC04LjgsNy4xLTE1LjksMTUuOS0xNS45aDgwLjhDMTEzLjIsMTEuNiwxMjAuMywxO\
C42LDEyMC4zLDI3LjV6Ii8+PHBhdGggZmlsbD0iI0YxQTEyRiIgZD0iTTEyMS45LDEwMWMwLjItNC4xL\
DAuNy03LjYsMS4yLTEwLjVjMi43LTEyLjcsMy41LTI2LjUsMi4zLTQxLjNjLTAuOS0xMS42LTMuMS0yN\
C41LTYuNi0zOC44QzEwNy41LDYuOSw5MC4yLDUuMyw2Nyw1LjVjLTI2LjEsMS00NC4zLDEuNy01NC40L\
DJjLTAuNCwwLjUtMS44LDEuOC00LjEsMy44Yy0xLjQsMS4zLTIuNSwyLjktMy4yLDQuOWMtMi4xLDMuM\
i0zLjIsOS0zLjMsMTcuNGMwLjMsOS44LDAuNCwxNi42LDAuNCwyMC41QzIuMSw3Mi45LDIsODMuNSwyL\
jIsODUuOGMxLDE1LjUsNC41LDI1LjMsMTAuNCwyOS41YzQuOCwzLjYsMTMuMiw1LjYsMjUsNmM2LjcsM\
C4yLDE2LjgsMC4xLDMwLjMtMC4yYzIuOCwwLDgtMC4yLDE1LjYtMC42YzYuNy0wLjEsMTEuMiwwLjUsM\
TMuNSwyYzcsMCwxMS45LTEsMTQuNi0yLjljNS43LTEuOCw4LjktNS4zLDkuNy0xMC40QzEyMS41LDEwO\
C4zLDEyMS43LDEwNS41LDEyMS45LDEwMSBNODMuNSwxMTMuMWMtNy42LDAuNC0xMi44LDAuNi0xNS42L\
DAuNmMtMTEuNiwwLjUtMjAuMywwLjgtMjYuMSwwLjljLTEwLjEsMC4xLTE4LjQtMS4yLTI0LjgtMy45Y\
y01LjMtOS04LjQtMjQuMy05LjQtNDYuMWMtMS0yMS43LDAuNy0zNy45LDUtNDguNmM4LjUtMy43LDE4L\
jQtNS41LDI5LjgtNS4yYzYuNSwwLjIsMTYuMSwwLjgsMjguOSwyYzMsMC4xLDE2LjUsMC4yLDQwLjMsM\
C4zbDEuNSw0LjRjMy4yLDQuMyw0LjgsMTYuNSw0LjksMzYuM2MwLjEsMTcuNC0wLjUsMjkuNS0yLDM2L\
jVjLTAuMywxLjUtMC40LDQuNC0wLjMsOC43YzAuMSwzLjUtMC4zLDYtMS4yLDcuNGMtMC44LDIuOC0xL\
jksNC42LTMuMSw1LjVjLTEuMywwLjktMy43LDEuNS03LjEsMS44Yy0wLjksMC41LTEuOSwwLjktMywxL\
jFjLTAuNiwwLjEtMiwwLjMtNC4zLDAuNEM5NC42LDExMy44LDkwLjEsMTEzLjEsODMuNSwxMTMuMXoiL\
z48L3N2Zz4=');
  }
  // Q Row Keys
  &.row2 {
    background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22128px%22%20height%3D%22128px%22%20viewBox%3D%220%200%201\
28%20128%22%20enable-background%3D%22new%200%200%20128%20128%22%20xml%3Aspace%3D\
%22preserve%22%3E%0A%3Cpath%20fill%3D%22%23FFF6CC%22%20d%3D%22M104.4%2C116.4H23.\
6c-8.8%2C0-15.9-7.1-15.9-15.9V27.5c0-8.8%2C7.1-15.9%2C15.9-15.9h80.8c8.8%2C0%2C1\
5.9%2C7.1%2C15.9%2C15.9%0A%09v73.1C120.3%2C109.3%2C113.2%2C116.4%2C104.4%2C116.4\
z%22/%3E%0A%3Cpath%20fill%3D%22%23FDDE26%22%20d%3D%22M121.9%2C27c-0.2-4.5-0.4-7.\
3-0.6-8.2c-0.8-5.1-4-8.6-9.7-10.4C108.9%2C6.5%2C104%2C5.5%2C97%2C5.5%0A%09c-2.3%\
2C1.5-6.8%2C2.1-13.5%2C2c-7.6-0.4-12.8-0.6-15.6-0.6C54.4%2C6.6%2C44.3%2C6.5%2C37\
.6%2C6.7c-11.8%2C0.4-20.2%2C2.4-25%2C6%0A%09c-5.9%2C4.2-9.4%2C14-10.4%2C29.5C2%2\
C44.5%2C2.1%2C55.1%2C2.4%2C73.9c0%2C3.9-0.1%2C10.7-0.4%2C20.5c0.1%2C8.4%2C1.2%2C\
14.2%2C3.3%2C17.4c0.7%2C2%2C1.8%2C3.6%2C3.2%2C4.9%0A%09c2.3%2C2%2C3.7%2C3.3%2C4.\
1%2C3.8c10.1%2C0.3%2C28.3%2C1%2C54.4%2C2c23.2%2C0.2%2C40.5-1.4%2C51.8-4.9c3.5-14\
.3%2C5.7-27.2%2C6.6-38.8c1.2-14.8%2C0.4-28.6-2.3-41.3%0A%09C122.6%2C34.6%2C122.1\
%2C31.1%2C121.9%2C27%20M97%2C12.8c2.3%2C0.1%2C3.7%2C0.3%2C4.3%2C0.4c1.1%2C0.2%2C\
2.1%2C0.6%2C3%2C1.1c3.4%2C0.3%2C5.8%2C0.9%2C7.1%2C1.8%0A%09c1.2%2C0.9%2C2.3%2C2.\
7%2C3.1%2C5.5c0.9%2C1.4%2C1.3%2C3.9%2C1.2%2C7.4c-0.1%2C4.3%2C0%2C7.2%2C0.3%2C8.7\
c1.5%2C7%2C2.1%2C19.1%2C2%2C36.5c-0.1%2C19.8-1.7%2C32-4.9%2C36.3%0A%09l-1.5%2C4.\
4c-23.8%2C0.1-37.3%2C0.2-40.3%2C0.3c-12.8%2C1.2-22.4%2C1.8-28.9%2C2c-11.4%2C0.3-\
21.3-1.5-29.8-5.2c-4.3-10.7-6-26.9-5-48.6%0A%09c1-21.8%2C4.1-37.1%2C9.4-46.1c6.4\
-2.7%2C14.7-4%2C24.8-3.9c5.8%2C0.1%2C14.5%2C0.4%2C26.1%2C0.9c2.8%2C0%2C8%2C0.2%2\
C15.6%2C0.6C90.1%2C14.9%2C94.6%2C14.2%2C97%2C12.8z%22%0A%09/%3E%0A%3C/svg%3E%0A');
  }
  // A Row Keys
  &.row3 {
    background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22128px%22%20height%3D%22128px%22%20viewBox%3D%220%200%201\
28%20128%22%20enable-background%3D%22new%200%200%20128%20128%22%20xml%3Aspace%3D\
%22preserve%22%3E%0A%3Cpath%20fill%3D%22%23DFEBBE%22%20d%3D%22M23.6%2C11.6h80.8c\
8.8%2C0%2C15.9%2C7.1%2C15.9%2C15.9v73.1c0%2C8.8-7.1%2C15.9-15.9%2C15.9H23.6c-8.8\
%2C0-15.9-7.1-15.9-15.9%0A%09V27.5C7.7%2C18.6%2C14.8%2C11.6%2C23.6%2C11.6z%22/%3\
E%0A%3Cpath%20fill%3D%22%23AED066%22%20d%3D%22M6.1%2C101c0.2%2C4.5%2C0.4%2C7.3%2\
C0.6%2C8.2c0.8%2C5.1%2C4%2C8.6%2C9.7%2C10.4c2.7%2C1.9%2C7.6%2C2.9%2C14.6%2C2.9c2\
.3-1.5%2C6.8-2.1%2C13.5-2%0A%09c7.6%2C0.4%2C12.8%2C0.6%2C15.6%2C0.6c13.5%2C0.3%2\
C23.6%2C0.4%2C30.3%2C0.2c11.8-0.4%2C20.2-2.4%2C25-6c5.9-4.2%2C9.4-14%2C10.4-29.5\
c0.2-2.3%2C0.1-12.9-0.2-31.7%0A%09c0-3.9%2C0.1-10.7%2C0.4-20.5c-0.1-8.4-1.2-14.2\
-3.3-17.4c-0.7-2-1.8-3.6-3.2-4.9c-2.3-2-3.7-3.3-4.1-3.8c-10.1-0.3-28.3-1-54.4-2%\
0A%09C37.8%2C5.3%2C20.5%2C6.9%2C9.2%2C10.4C5.7%2C24.7%2C3.5%2C37.6%2C2.6%2C49.2C\
1.4%2C64%2C2.2%2C77.8%2C4.9%2C90.5C5.4%2C93.4%2C5.9%2C96.9%2C6.1%2C101%20M31%2C1\
15.2%0A%09c-2.3-0.1-3.7-0.3-4.3-0.4c-1.1-0.2-2.1-0.6-3-1.1c-3.4-0.3-5.8-0.9-7.1-\
1.8c-1.2-0.9-2.3-2.7-3.1-5.5c-0.9-1.4-1.3-3.9-1.2-7.4%0A%09c0.1-4.3%2C0-7.2-0.3-\
8.7c-1.5-7-2.1-19.1-2-36.5c0.1-19.8%2C1.7-32%2C4.9-36.3l1.5-4.4c23.8-0.1%2C37.3-\
0.2%2C40.3-0.3%0A%09c12.8-1.2%2C22.4-1.8%2C28.9-2c11.4-0.3%2C21.3%2C1.5%2C29.8%2\
C5.2c4.3%2C10.7%2C6%2C26.9%2C5%2C48.6c-1%2C21.8-4.1%2C37.1-9.4%2C46.1c-6.4%2C2.7\
-14.7%2C4-24.8%2C3.9%0A%09c-5.8-0.1-14.5-0.4-26.1-0.9c-2.8%2C0-8-0.2-15.6-0.6C37\
.9%2C113.1%2C33.4%2C113.8%2C31%2C115.2z%22/%3E%0A%3C/svg%3E%0A');
  }
  // Z Row Keys
  &.row4 {
    background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22128px%22%20height%3D%22128px%22%20viewBox%3D%220%200%201\
28%20128%22%20enable-background%3D%22new%200%200%20128%20128%22%20xml%3Aspace%3D\
%22preserve%22%3E%0A%3Cpath%20fill%3D%22%23D2E6EF%22%20d%3D%22M120.3%2C27.5v73.1\
c0%2C8.8-7.1%2C15.9-15.9%2C15.9H23.6c-8.8%2C0-15.9-7.1-15.9-15.9V27.5c0-8.8%2C7.\
1-15.9%2C15.9-15.9%0A%09h80.8C113.2%2C11.6%2C120.3%2C18.6%2C120.3%2C27.5z%22/%3E\
%0A%3Cpath%20fill%3D%22%238DB6D2%22%20d%3D%22M121.9%2C101c0.2-4.1%2C0.7-7.6%2C1.\
2-10.5c2.7-12.7%2C3.5-26.5%2C2.3-41.3c-0.9-11.6-3.1-24.5-6.6-38.8%0A%09C107.5%2C\
6.9%2C90.2%2C5.3%2C67%2C5.5c-26.1%2C1-44.3%2C1.7-54.4%2C2c-0.4%2C0.5-1.8%2C1.8-4\
.1%2C3.8c-1.4%2C1.3-2.5%2C2.9-3.2%2C4.9c-2.1%2C3.2-3.2%2C9-3.3%2C17.4%0A%09c0.3%\
2C9.8%2C0.4%2C16.6%2C0.4%2C20.5C2.1%2C72.9%2C2%2C83.5%2C2.2%2C85.8c1%2C15.5%2C4.\
5%2C25.3%2C10.4%2C29.5c4.8%2C3.6%2C13.2%2C5.6%2C25%2C6c6.7%2C0.2%2C16.8%2C0.1%2C\
30.3-0.2%0A%09c2.8%2C0%2C8-0.2%2C15.6-0.6c6.7-0.1%2C11.2%2C0.5%2C13.5%2C2c7%2C0%\
2C11.9-1%2C14.6-2.9c5.7-1.8%2C8.9-5.3%2C9.7-10.4C121.5%2C108.3%2C121.7%2C105.5%2\
C121.9%2C101%0A%09%20M83.5%2C113.1c-7.6%2C0.4-12.8%2C0.6-15.6%2C0.6c-11.6%2C0.5-\
20.3%2C0.8-26.1%2C0.9c-10.1%2C0.1-18.4-1.2-24.8-3.9c-5.3-9-8.4-24.3-9.4-46.1%0A%\
09c-1-21.7%2C0.7-37.9%2C5-48.6c8.5-3.7%2C18.4-5.5%2C29.8-5.2c6.5%2C0.2%2C16.1%2C\
0.8%2C28.9%2C2c3%2C0.1%2C16.5%2C0.2%2C40.3%2C0.3l1.5%2C4.4%0A%09c3.2%2C4.3%2C4.8\
%2C16.5%2C4.9%2C36.3c0.1%2C17.4-0.5%2C29.5-2%2C36.5c-0.3%2C1.5-0.4%2C4.4-0.3%2C8\
.7c0.1%2C3.5-0.3%2C6-1.2%2C7.4c-0.8%2C2.8-1.9%2C4.6-3.1%2C5.5%0A%09c-1.3%2C0.9-3\
.7%2C1.5-7.1%2C1.8c-0.9%2C0.5-1.9%2C0.9-3%2C1.1c-0.6%2C0.1-2%2C0.3-4.3%2C0.4C94.\
6%2C113.8%2C90.1%2C113.1%2C83.5%2C113.1z%22/%3E%0A%3C/svg%3E%0A');
  }
  // Bottom Row Keys (Spacebar)
  &.row5 {
    &,
    &.error {
      background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22134px%22%20height%3D%2260px%22%20viewBox%3D%220%200%2013\
4%2060%22%20enable-background%3D%22new%200%200%20134%2060%22%20xml%3Aspace%3D%22\
preserve%22%3E%0A%3Cpath%20fill%3D%22%23D4D1E3%22%20d%3D%22M124.1%2C58.6H10.8c-4\
.6%2C0-8.3-3.7-8.3-8.3V10.4c0-4.6%2C3.7-8.3%2C8.3-8.3h113.3c4.6%2C0%2C8.3%2C3.7%\
2C8.3%2C8.3v39.9%0A%09C132.4%2C54.9%2C128.7%2C58.6%2C124.1%2C58.6z%22/%3E%0A%3Cp\
ath%20fill%3D%22%23958DB9%22%20d%3D%22M131.5%2C4.3c-1.7-2.2-5.4-3.6-11.1-4.1c-0.\
2%2C0-4.6-0.1-13.2-0.2c-1.2%2C0.2-84.2%2C0.7-87.1%2C0.5%0A%09c-6.1-0.5-10.6%2C0-\
13.7%2C1.6C1.2%2C4.7-0.8%2C13.8%2C0.3%2C29.2c0.1%2C0.8%2C0.2%2C3.2%2C0.3%2C7.4c0\
.1%2C2.8%2C0.3%2C4.9%2C0.7%2C6.4c0.3%2C1.2%2C0.3%2C3.7%2C0%2C7.6%0A%09c-0.2%2C3%\
2C0.2%2C5.2%2C1.3%2C6.6c0.3%2C0.5%2C0.9%2C0.9%2C1.8%2C1.3c1.2%2C0.5%2C3.1%2C0.9%\
2C5.5%2C1.2c3.1%2C0.4%2C5.5%2C0.5%2C7.2%2C0.3c1.1-0.1-24.3-0.2%2C24.4-0.2%0A%09c\
0%2C0%2C64.5-0.4%2C66.2-0.8c0.1%2C0-0.4%2C0-1.5-0.1c7.7-0.1%2C11.7-0.2%2C11.9-0.\
3c0.4%2C0.1%2C1.9%2C0.2%2C4.5%2C0.1c2.4-0.1%2C4-0.2%2C4.7-0.4%0A%09c0.1%2C0%2C0.\
1%2C0%2C0.2-0.1c1.2-0.4%2C2.2-0.9%2C2.9-1.4c0.4-0.4%2C1-1.1%2C1.8-2.1c0.1-0.5%2C\
0.2-1%2C0.5-1.7c0.3-0.8%2C0.4-1.3%2C0.5-1.5%0A%09c0.3-1.1%2C0.2-2.7-0.3-5c-0.2-1\
-0.3-2.4-0.1-4.2c0.2-1.9%2C0.4-3.2%2C0.5-4c-0.2-1.2-0.2-2.2-0.1-2.8c0.5-2.4%2C0.\
8-5%2C0.9-8%0A%09c0-2.9-0.2-5.6-0.6-8c0-0.4%2C0-1.4%2C0-3.1c-0.1-1.7-0.1-2.7-0.1\
-3.1c0-0.2-0.2-1.9-0.4-5C132.9%2C6.8%2C132.4%2C5.3%2C131.5%2C4.3%20M110.8%2C3.4%\
0A%09c0.9%2C0.2%2C3.7%2C0.3%2C8.5%2C0.4c3.5%2C0.1%2C6.5%2C0.7%2C8.8%2C1.8c2.8%2C\
10.6%2C3.4%2C19.6%2C1.9%2C27c-0.2%2C0.8-0.2%2C2-0.2%2C3.8c0%2C1.7%2C0%2C2.9-0.2%\
2C3.5%0A%09c-0.2%2C1-0.2%2C3.4%2C0.1%2C7c0.2%2C3%2C0%2C5.1-0.8%2C6.2c-0.2%2C0.4-\
0.4%2C0.8-0.7%2C1.1c-0.5%2C0.4-0.8%2C0.7-0.9%2C0.9c-3.3%2C0.2-5.8%2C0.5-7.5%2C1H\
107%0A%09c-63.1%2C0.1-3.9%2C0.3-63.7%2C0.4l-14%2C0.2c-1.1-0.1-2-0.2-2.6-0.2c-1.1\
-0.1-2.1%2C0-3.1%2C0.2c-1.6%2C0.4-4.3%2C0.5-8.1%2C0.4%0A%09c-3.2-0.1-5.2-0.2-6.1\
-0.4c-0.1%2C0-0.7-0.1-1.8-0.2c-0.5%2C0-1-0.2-1.4-0.5C5.4%2C55.4%2C5%2C54.5%2C4.9\
%2C53c-0.1-2.5-0.2-3.9-0.2-4.1%0A%09c-0.3-2.4-0.6-4.1-1-5c0-0.3%2C0-1.5-0.1-3.5c\
-0.1-1.9-0.1-3.1%2C0-3.5c-0.3-2.7-0.5-4.3-0.5-4.7c-0.1-1.5-0.1-3.4-0.1-5.7%0A%09\
c0.3-1.3%2C0.4-2.3%2C0.3-2.8c-0.6-3.3-0.6-6.2%2C0-8.8c0.3-2.3%2C0.6-4%2C0.8-5.1C\
4.6%2C8.2%2C5.1%2C6.8%2C6%2C5.9c0.6-0.7%2C1.7-1.1%2C3.3-1.4%0A%09c1.8-0.3%2C3-0.\
7%2C3.7-1.1c4.5%2C0%2C5.6%2C1%2C7.3%2C1c12.9%2C0%2C19.9-0.2%2C20.9-0.4C64%2C3.9%\
2C109.6%2C3.2%2C110.8%2C3.4z%22/%3E%0A%3C/svg%3E%0A');
    }
    &.spacebar {
      // padding: remValues(45, 70);
      padding: 40px 70px;
      // margin: remValues(0, 5);
      margin: 0 5px;
    }
  }
  &.error {
    background-image: url('data:image/svg+xml;\
charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%\
0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plu\
g-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%\
3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20\
xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220p\
x%22%0A%09%20width%3D%22128px%22%20height%3D%22128px%22%20viewBox%3D%220%200%201\
28%20128%22%20enable-background%3D%22new%200%200%20128%20128%22%20xml%3Aspace%3D\
%22preserve%22%3E%0A%3Cpath%20fill%3D%22%23CB2026%22%20d%3D%22M125.1%2C35c-0.5-3\
.2-1-7-1.2-11.5c-0.2-4.9-0.4-8-0.6-9c-0.8-5.6-4.1-9.4-10-11.4C110.4%2C1.1%2C105.\
3%2C0%2C98.1%2C0%0A%09c-2.4%2C1.6-7%2C2.3-13.9%2C2.2c-7.8-0.4-13.2-0.7-16.1-0.7C\
54.1%2C1.2%2C43.7%2C1.1%2C36.8%2C1.3C24.6%2C1.8%2C15.9%2C3.9%2C10.9%2C7.9%0A%09C\
4.9%2C12.5%2C1.2%2C23.2%2C0.2%2C40.1C0%2C42.7%2C0.1%2C54.3%2C0.4%2C74.8c0%2C4.3-\
0.1%2C11.7-0.4%2C22.4c0.1%2C9.2%2C1.2%2C15.5%2C3.4%2C19c0.7%2C2.2%2C1.9%2C3.9%2C\
3.3%2C5.4%0A%09c2.4%2C2.2%2C3.8%2C3.6%2C4.2%2C4.2c10.4%2C0.3%2C29.2%2C1.1%2C56.2\
%2C2.2c24%2C0.2%2C41.8-1.5%2C53.5-5.4c3.6-15.6%2C5.9-29.8%2C6.8-42.4%0A%09C128.7\
%2C64%2C127.9%2C48.9%2C125.1%2C35z%22/%3E%0A%3C/svg%3E%0A');
  }
}

// STYLES FOR isSimple
.simple {
  margin-top: 0;
  margin-bottom: 0;
  display: inline-block;
  text-align: center;
  font-family: 'hwtprint';
  position: relative;
  top: 50%;
  transform: translateY(-50%);

  > span {
    color: #888888;
    border: 1px solid white;
    border-radius: 5px;
    display: inline-block;
    text-align: center;
    padding: 2px 0 2px 2px;

    &.charDone {
      color: black;
    }
  }

  > .expected {
    border: 1px solid black;
    border-radius: 4px;
    color: black;
    padding: 2px 2px 4px;
    &.row1 {
      background-color: #fad4a4;
      background-image: none;
    }
    &.row2 {
      background-color: #fff6cb;
      background-image: none;
    }
    &.row3 {
      background-color: #dfebbe;
      background-image: none;
    }
    &.row4 {
      background-color: #d2e6ee;
      background-image: none;
    }
  }
}
</style>
